import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import type { SupportedSoftware } from "@domain/repositories";
import { useInjection } from "@presentation/components/dependencyInjection";
import { spectronautVersionsSeenByUserStoragePrefix } from "@presentation/components/helpers/constants";
import { useProjectPermission, useTeamPermission } from "@presentation/components/helpers/usePermissionCheck";
import { selectPermissionEntry, usePermissionStore } from "@presentation/stores/PermissionStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { settingsUrl } from "@presentation/common/settingsRoutes";
import { useLicensedSoftware } from "@presentation/components/job/components/useLicensedSoftware";
import { ProjectActions, TeamActions } from "@domain/schemas/workspace";

const SOFTWARE: SupportedSoftware[] = ["Spectronaut", "SpectroMine"];
const normalizeVersions = (versions: string[]) => [...new Set(versions.map((version) => version.trim()).filter(Boolean))];

export const NewSoftwareVersionsNotification: React.FC<{ username: string }> = ({ username }) => {
  const versionsService = useRef(useInjection(ListSoftwareVersionsService)).current;
  const navigate = useNavigate();
  const [discovered, setDiscovered] = useState<Partial<Record<SupportedSoftware, string[]>>>({});
  const [dismissed, setDismissed] = useState(false);
  const licenseState = useLicensedSoftware();
  const licensedSoftware = licenseState.status === "ready" ? licenseState.licensed : null;
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const activeTeamId = useWorkspaceContextStore((state) => state.activeTeamId);
  const project = useWorkspaceContextStore((state) => state.projects.find((candidate) => candidate.id === state.activeProjectId));
  const teamId = project?.teamId ?? activeTeamId;
  const canManageProject = useProjectPermission(project?.id, ProjectActions.ManageSoftwareVersions).allowed;
  const canManageTeam = useTeamPermission(teamId, TeamActions.ManageSoftwareVersions).allowed;
  const projectEntry = usePermissionStore(selectPermissionEntry({ projectId: activeProjectId }));
  const teamEntry = usePermissionStore(selectPermissionEntry({ teamId }));
  const isAdmin = usePermissionStore((state) => state.isAdmin);
  const workspaceStatus = useWorkspaceContextStore((state) => state.status);
  const isUnresolved =
    workspaceStatus !== "loaded" || Boolean(activeProjectId && (!project || projectEntry.status !== "loaded")) || Boolean(!canManageProject && teamId && teamEntry.status !== "loaded");

  useEffect(() => {
    let isCancelled = false;
    const softwareToCheck = licensedSoftware ?? [];
    for (const software of softwareToCheck) {
      void (async () => {
        try {
          const versions = normalizeVersions(await versionsService.getCandidateVersionsListAsync(software));
          if (isCancelled || versions.length === 0) return;
          // Preserve the existing Spectronaut baseline when upgrading.
          const prefix = software === "Spectronaut" ? spectronautVersionsSeenByUserStoragePrefix : "spectromineVersionsSeenByUser";
          const key = `${prefix}:${username}`;
          const rawSeen = localStorage.getItem(key);
          let seen: string[] | null = null;
          if (rawSeen) {
            try {
              const parsed: unknown = JSON.parse(rawSeen);
              if (Array.isArray(parsed) && parsed.every((value): value is string => typeof value === "string")) seen = normalizeVersions(parsed);
            } catch (error) {
              console.warn(`Invalid ${software} version notification baseline`, error);
            }
          }
          const newVersions = seen === null ? [] : versions.filter((version) => !seen.includes(version));
          if (newVersions.length > 0) setDiscovered((previous) => ({ ...previous, [software]: newVersions }));
          localStorage.setItem(key, JSON.stringify(versions));
        } catch (error) {
          console.warn(`Unable to check ${software} image tags`, error);
        }
      })();
    }
    return () => {
      isCancelled = true;
    };
  }, [username, versionsService, licensedSoftware]);

  if (dismissed || !licensedSoftware?.some((software) => discovered[software]?.length)) return null;

  const target = (() => {
    if (isUnresolved) return null;
    const tab = new URLSearchParams({ tab: "software-versions" });
    if (canManageProject && project) return { path: settingsUrl({ level: "project", projectId: project.id }, tab), label: "Configure project versions" };
    if (canManageTeam && teamId) return { path: settingsUrl({ level: "team", teamId }, tab), label: "Configure team versions" };
    return isAdmin ? { path: settingsUrl({ level: "deployment" }, tab), label: "Configure deployment versions" } : null;
  })();

  return (
    <Dialog title="New software versions discovered" width={520} onClose={() => setDismissed(true)}>
      {SOFTWARE.map((software) => {
        const versions = discovered[software];
        if (!versions || !licensedSoftware.includes(software)) return null;
        return (
          <p key={software}>
            New {software} {versions.length === 1 ? "version" : "versions"} discovered: {versions.slice(0, 3).join(", ")}
            {versions.length > 3 ? `, and ${versions.length - 3} more` : ""}.
          </p>
        );
      })}
      <p>Registry discovery does not mean a version is enabled for this project. Availability depends on its inherited software-version configuration.</p>
      {!target && <p>{isUnresolved ? "Configuration permissions could not yet be verified." : "Contact a project or team manager or an administrator to manage version availability."}</p>}
      <DialogActionsBar>
        <Button type="button" onClick={() => setDismissed(true)}>
          Dismiss
        </Button>
        {target && (
          <Button
            type="button"
            themeColor="primary"
            onClick={() => {
              setDismissed(true);
              navigate(target.path);
            }}
          >
            {target.label}
          </Button>
        )}
      </DialogActionsBar>
    </Dialog>
  );
};
